import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { toast } from "sonner";

import ProjectAPI from "../../api/ProjectAPI";

import type { UseMutateFunction } from "@tanstack/react-query";

import type {
  CreateStorageAccessResponse,
  GetStorageAccessListResponse,
  StorageAccessPayload,
  StorageTestStatus,
} from "../../api/ProjectAPI.types";

interface UpdateStorageAccessVariables {
  uuid: string;
  data: StorageAccessPayload;
}

export interface UseStorageAccessMutationsResult {
  createStorageAccess: (
    data: StorageAccessPayload,
  ) => Promise<CreateStorageAccessResponse>;
  updateStorageAccess: (
    variables: UpdateStorageAccessVariables,
  ) => Promise<void>;
  deleteStorageAccess: UseMutateFunction<void, unknown, string>;
  testStorageAccessConnection: (uuid: string) => Promise<void>;
  testStorageAccessCredentials: (
    data: StorageAccessPayload,
    uuid?: string,
  ) => Promise<void>;
  isSaving: boolean;
  isDeleting: boolean;
}

export function useStorageAccessMutations(): UseStorageAccessMutationsResult {
  const queryClient = useQueryClient();

  const listQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getStorageAccessList,
    params: [],
  });

  const invalidateList = (): void => {
    queryClient.invalidateQueries(listQuery.queryKey);
  };

  // Write a connection-test outcome straight into the cached list instead of
  // refetching. The /test endpoint doesn't return `last_tested_at`, so the
  // client timestamp is used (a negligible drift from the server's).
  const setTestStatus = (uuid: string, status: StorageTestStatus): void => {
    queryClient.setQueryData<GetStorageAccessListResponse>(
      listQuery.queryKey,
      (previous) => {
        if (!previous) return previous;
        const now = new Date().toISOString();
        return {
          ...previous,
          data: previous.data.map((item) =>
            item.uuid === uuid
              ? { ...item, last_test_status: status, last_tested_at: now }
              : item,
          ),
        };
      },
    );
  };

  // Create/update leave error-toasting to the form (it maps field errors); both
  // refetch the list on success (one consistent strategy — the server owns the
  // single-default reshuffle, so a refetch is the authoritative source).
  const onSaveSuccess = (): void => {
    toast.success(t("editor-notif:saveSuccess"));
    invalidateList();
  };

  const createMutation = useMutation({
    mutationFn: async (data: StorageAccessPayload) =>
      ProjectAPI.createStorageAccess(data),
    onSuccess: onSaveSuccess,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ uuid, data }: UpdateStorageAccessVariables) =>
      ProjectAPI.updateStorageAccess({ uuid, data }),
    onSuccess: onSaveSuccess,
  });

  const deleteMutation = useMutation({
    mutationFn: async (uuid: string) => ProjectAPI.deleteStorageAccess(uuid),
    onSuccess: () => {
      toast.success(t("editor-notif:deleteSuccess"));
      invalidateList();
    },
    onError: () => {
      toast.error(t("editor-notif:deleteError"));
    },
  });

  const testMutation = useMutation({
    mutationFn: async (uuid: string) =>
      ProjectAPI.testStorageAccessConnection({ uuid }),
    onSuccess: () => {
      toast.success(t("editor-organization:storageAccess.notif.testSuccess"));
    },
    onError: () => {
      toast.error(t("editor-organization:storageAccess.notif.testError"));
    },
  });

  // Test of the credentials currently in the form: a create draft, or the
  // unsaved edits of an existing record (via its uuid, reusing the stored
  // secret when omitted). It reflects unsaved state, so the list cache / saved
  // status pill is left untouched — only a toast surfaces the outcome.
  const testCredentialsMutation = useMutation({
    mutationFn: async ({
      data,
      uuid,
    }: {
      data: StorageAccessPayload;
      uuid?: string;
    }) =>
      uuid
        ? ProjectAPI.testStorageAccessConnection({ uuid, data })
        : ProjectAPI.testStorageAccessCredentials(data),
    onSuccess: () => {
      toast.success(t("editor-organization:storageAccess.notif.testSuccess"));
    },
    onError: () => {
      toast.error(t("editor-organization:storageAccess.notif.testError"));
    },
  });

  return {
    createStorageAccess: async (data: StorageAccessPayload) =>
      createMutation.mutateAsync(data),
    updateStorageAccess: async (variables: UpdateStorageAccessVariables) =>
      updateMutation.mutateAsync(variables),
    deleteStorageAccess: deleteMutation.mutate,
    testStorageAccessConnection: async (uuid: string) => {
      try {
        await testMutation.mutateAsync(uuid);
        setTestStatus(uuid, "success");
      } catch (error) {
        setTestStatus(uuid, "failed");
        throw error;
      }
    },
    testStorageAccessCredentials: async (
      data: StorageAccessPayload,
      uuid?: string,
    ) => testCredentialsMutation.mutateAsync({ data, uuid }),
    isSaving: createMutation.isLoading || updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
}
